import { useQueryUser } from '@heidi/workspace/data';

import { getComplimentaryDaysLeft } from '../../data/query/complimentary-trial-end.utils';
import { isComplimentaryCreditAllowance } from '../../data/query/credit-allowance.utils';
import { useQueryCreditAllowance } from '../../data/query/useQueryCreditAllowance';
import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { resolveYearlyMaxComplimentaryDiscount } from '../../data/yearly-max-complimentary-discount';
import { isHeidi2OptInCreditUsageEnabled } from './is-credit-usage-surface-enabled';

export type ComplimentaryYearlyMaxOffer = {
  cycleEnd: string;
  /** Catalog discount to send when the checkout target is yearly Max. */
  applyDiscountForYearlyMax: ReturnType<typeof resolveYearlyMaxComplimentaryDiscount>;
};

/**
 * Yearly Max complimentary offer while the uncapped Heidi 2 cycle is still
 * live. Broader than the T-7 countdown: the coupon and seat-page banner stay
 * available for the whole complimentary window.
 */
export function useComplimentaryYearlyMaxOffer({
  isHeidi2OptInEnabled,
  isTrialEndEnabled,
}: {
  isHeidi2OptInEnabled: boolean;
  isTrialEndEnabled: boolean;
}): ComplimentaryYearlyMaxOffer | null {
  const { data: user } = useQueryUser();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const isCohortEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  const { data: allowance } = useQueryCreditAllowance({
    hasUser: !!user,
    enabled: isTrialEndEnabled && isHeidi2OptInEnabled && !user?.is_managed,
  });

  if (!isTrialEndEnabled || !isCohortEnabled || user?.is_managed) {
    return null;
  }
  if (allowance == null || !isComplimentaryCreditAllowance(allowance)) {
    return null;
  }
  if (getComplimentaryDaysLeft(allowance, new Date()) == null) {
    return null;
  }

  return {
    cycleEnd: allowance.cycle_end,
    applyDiscountForYearlyMax: resolveYearlyMaxComplimentaryDiscount({
      isTrialEndEnabled: true,
      isComplimentaryUsage: true,
      isYearlyMaxTarget: true,
    }),
  };
}
